/* RNF native cart: one mutation queue; Shopify owns all prices and discounts. */
(() => {
  'use strict';
  if (window.RNFCart) return;
  const root = document.getElementById('RnfCartDrawer');
  const configNode = root && root.querySelector('[data-rnf-cart-config]');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const sectionIds = ['rnf-native-cart', 'cart-icon-bubble'];
  let cart = readCart(root);
  let sections = null;
  let sectionDocument = null;
  let renderedCart = cart;
  let renderedHTML = null;
  let pointerInput = false;
  let queue = Promise.resolve();
  let pending = 0;
  let drawer;
  let viewReady = true;
  let timerId;
  let timerEnd;
  let cartConfirmedAt = 0;
  let checkoutPending = false;
  let approvedCheckoutForm = null;
  let approvedCheckoutEvent = null;
  let checkoutAttempt = 0;
  let protectionIntent = null;
  const quantityEdits = [];
  const recommendationIntents = new Map();
  let resumeSync = null;
  const continuingKey = 'rnf:continue-shopping:v1';
  let continuingShopping = false;
  try { continuingShopping = sessionStorage.getItem(continuingKey) === '1'; } catch (_) {}

  function hasWatches() {
    return Boolean(cart?.items.some(item => item.quantity > 0 && config.gifts.some(rule => rule.products.includes(String(item.product_id)))));
  }

  function rememberContinuingShopping(value) {
    continuingShopping = value;
    try { if (value) sessionStorage.setItem(continuingKey, '1'); else sessionStorage.removeItem(continuingKey); } catch (_) {}
  }

  function readCart(node) {
    const state = node.querySelector('[data-rnf-cart-state]');
    if (!state) return null;
    try { return JSON.parse(state.textContent); } catch (_) { return null; }
  }

  function parseSection(html) {
    // Template content stays inert: parsing must not start detached image requests.
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content;
  }

  function accept(payload) {
    sectionDocument = null;
    if (payload.sections) {
      sections = payload.sections;
      const html = sections['rnf-native-cart'];
      sectionDocument = html ? parseSection(html) : null;
      cart = sectionDocument && readCart(sectionDocument);
      if (!cart && Array.isArray(payload.items) && typeof payload.item_count === 'number') cart = payload;
    } else if (Array.isArray(payload.items) && typeof payload.item_count === 'number') {
      cart = payload;
      sections = null;
    } else {
      // add.js can contain only the added lines; it is not a canonical cart.
      cart = null;
      sections = null;
    }
    cartConfirmedAt = cart ? Date.now() : 0;
    return payload;
  }

  function readDeadline(signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    return { signal: controller.signal, clear() { clearTimeout(timer); signal?.removeEventListener('abort', abort); } };
  }

  async function request(url, body) {
    const init = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      viewReady = false;
      init.method = 'POST';
      if (body instanceof FormData) {
        body.set('sections', sectionIds.join(','));
        body.set('sections_url', location.pathname);
        init.body = body;
      } else {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify({ ...body, sections: sectionIds, sections_url: location.pathname });
      }
    } else {
      init.cache = 'no-store';
    }
    // Bound reads, including JSON decoding. Mutations keep their existing
    // ambiguity/recovery semantics and are never automatically retried.
    const deadline = body === undefined ? readDeadline() : null;
    if (deadline) init.signal = deadline.signal;
    try {
      const response = await fetch(url, init);
      const payload = await response.json();
      if (!response.ok || payload.status >= 400) {
        const error = new Error(payload.description || payload.message || config.error);
        error.payload = payload;
        error.status = response.status;
        throw error;
      }
      return accept(payload);
    } finally {
      deadline?.clear();
    }
  }

  const api = {
    async getCart() { await request(config.cart); return cart; },
    currentCart,
    async add(body) { return request(config.add, body); },
    async change(body) { return request(config.change, body); }
  };

  async function currentCart() {
    if (!cart || !cartConfirmedAt || Date.now() - cartConfirmedAt > 5000) await api.getCart();
    return cart;
  }

  function isGiftOnlyVariant(variantId, settings = config) {
    const variants = Array.isArray(settings?.giftOnlyVariants) ? settings.giftOnlyVariants : ['52465283137877', '52465285824853', '53514098016597', '53592219910485', '53592219943253'];
    return new Set(variants.map(value => String(value).replace(/^.*\//, ''))).has(String(variantId).replace(/^.*\//, ''));
  }

  function hasRealItems(state = cart) {
    return Boolean(state?.items.some(item => item.quantity > 0 && String(item.variant_id) !== String(config.protection)
      && !isGiftOnlyVariant(item.variant_id)
      && !config.gifts.some(rule => String(rule.variant) === String(item.variant_id))));
  }

  function giftQuantity(rule, state = cart) {
    return state.items.reduce((quantity, item) => rule.products.includes(String(item.product_id)) ? quantity + item.quantity : quantity, 0);
  }

  function cleanupRemovals(state) {
    const updates = {};
    // Historical free products have no current automatic-gift rule.
    state.items.filter(item => isGiftOnlyVariant(item.variant_id)
      && !config.gifts.some(rule => String(rule.variant) === String(item.variant_id)))
      .forEach(item => { updates[item.key] = 0; });
    for (const rule of config.gifts) {
      const eligible = giftQuantity(rule, state) > 0;
      const gifts = state.items.filter(item => String(item.variant_id) === String(rule.variant));
      gifts.slice(eligible ? 1 : 0).forEach(item => { updates[item.key] = 0; });
    }
    if (!hasRealItems(state)) state.items.filter(item => String(item.variant_id) === String(config.protection)).forEach(item => { updates[item.key] = 0; });
    return updates;
  }

  async function removeLines(updates) {
    // update.js bypasses inventory validation for increases. Only exact zeroes use it.
    if (config.update && Object.keys(updates).length > 1) return request(config.update, { updates });
    const key = Object.keys(updates)[0];
    return key ? api.change({ id: key, quantity: 0 }) : cart;
  }

  // The two included boxes are already free in Shopify. Never alter their price.
  async function reconcileGifts() {
    if (!cart) await api.getCart();
    const errors = [];
    // Re-read keys after each removal; one legacy line also needs cleaning.
    let cleanupBudget = cart.items.length;
    while (cleanupBudget-- > 0) {
      const removals = cleanupRemovals(cart);
      if (!Object.keys(removals).length) break;
      try { await removeLines(removals); if (!cart) await api.getCart(); }
      catch (error) { errors.push(error.message || config.error); await api.getCart(); break; }
    }
    for (const rule of config.gifts) {
      try {
      const requiredQuantity = giftQuantity(rule);
      const eligible = requiredQuantity > 0;
      const matching = () => cart.items.filter(item => String(item.variant_id) === rule.variant);
      // Discounts can change every line key after any mutation. Re-read each key.
      let gifts = matching();
      let remaining = gifts.length + 1;
      while (gifts.length > (eligible ? 1 : 0) && remaining-- > 0) {
        await api.change({ id: gifts[gifts.length - 1].key, quantity: 0 });
        if (!cart) await api.getCart();
        gifts = matching();
      }
      const keep = eligible && gifts[0];
      if (keep && keep.quantity !== requiredQuantity) {
        await api.change({ id: keep.key, quantity: requiredQuantity });
        if (!cart) await api.getCart();
      }
      if (eligible && !keep) {
        if (!rule.available) throw new Error(config.error);
        await api.add({ items: [{ id: rule.variant, quantity: requiredQuantity, properties: { _rnf_auto_gift: rule.id } }] });
        if (!cart) await api.getCart();
      }
      gifts = matching();
      if (gifts.length !== (eligible ? 1 : 0) || (eligible && gifts[0].quantity !== requiredQuantity)) throw new Error(config.error);
      } catch (error) {
        errors.push(error.message || config.error);
        // Confirm a possibly successful write before processing the other rule.
        await api.getCart();
      }
    }
    if (!hasRealItems()) {
      try {
        let remaining = cart.items.filter(item => String(item.variant_id) === String(config.protection)).length;
        while (remaining-- > 0) {
          const line = cart.items.find(item => String(item.variant_id) === String(config.protection));
          if (!line) break;
          await api.change({ id: line.key, quantity: 0 });
          if (!cart) await api.getCart();
        }
      } catch (error) {
        errors.push(error.message || config.error);
        await api.getCart();
      }
    }
    if (Object.keys(cleanupRemovals(cart)).length) errors.push(config.error);
    if (errors.length) throw new Error([...new Set(errors)].join(' '));
  }

  async function fetchSections(signal) {
    const deadline = readDeadline(signal);
    try {
    const url = new URL(location.href);
    url.searchParams.set('sections', sectionIds.join(','));
    // Shopify negotiates product JSON on product URLs if Accept is application/json.
    // Section Rendering needs the default HTML-capable Accept header instead.
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: deadline.signal });
    if (!response.ok) throw new Error(`Section rendering HTTP ${response.status}`);
    const nextSections = await response.json();
    const html = nextSections['rnf-native-cart'];
    if (!html) throw new Error(`Missing native section; returned: ${Object.keys(nextSections).join(', ')}`);
    const parsed = parseSection(html);
    const state = readCart(parsed);
    if (!state) throw new Error('Missing canonical cart state');
    if (deadline.signal.aborted) throw new DOMException('Cart synchronization cancelled', 'AbortError');
    sections = nextSections;
    sectionDocument = parsed;
    cart = state;
    cartConfirmedAt = Date.now();
    } finally {
      deadline.clear();
    }
  }

  function needsGiftReconciliation() {
    if (!cart) return true;
    if (Object.keys(cleanupRemovals(cart)).length) return true;
    return config.gifts.some(rule => {
      const requiredQuantity = giftQuantity(rule);
      if (!requiredQuantity) return false;
      const gifts = cart.items.filter(item => String(item.variant_id) === String(rule.variant));
      return gifts.length !== 1 || gifts[0].quantity !== requiredQuantity;
    });
  }

  function syncCart() {
    if (document.hidden || (drawer && !drawer.isConnected)) return Promise.resolve(cart);
    if (resumeSync && !resumeSync.controller.signal.aborted) return resumeSync.promise;
    const sync = { controller: new AbortController(), promise: null };
    resumeSync = sync;
    sync.promise = run(() => fetchSections(sync.controller.signal), { open: false, reconcile: false, recover: false, quiet: true, strict: true }).then(() => cart).finally(() => {
      if (resumeSync === sync) resumeSync = null;
    });
    return sync.promise;
  }

  function suspendSync() {
    cartConfirmedAt = 0;
    resumeSync?.controller.abort();
  }

  function paintCartBubble() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !cart) return;
    const count = Number(cart.item_count);
    if (!Number.isSafeInteger(count) || count < 0) return;
    let badge = bubble.querySelector('.cart-count-bubble');
    if (count === 0) { badge?.remove(); return; }
    const visible = badge?.querySelector('span[aria-hidden="true"]');
    if (sections?.['cart-icon-bubble'] && badge && (count >= 100 ? !visible : Number(visible?.textContent) === count)) return;
    if (!badge) {
      badge = document.createElement('div');
      badge.className = 'cart-count-bubble';
      bubble.append(badge);
    }
    let number = badge.querySelector('span[aria-hidden="true"]');
    if (!number) { number = document.createElement('span'); number.setAttribute('aria-hidden', 'true'); badge.append(number); }
    number.hidden = count >= 100;
    number.textContent = String(count);
    let spoken = badge.querySelector('.visually-hidden');
    if (!spoken) { spoken = document.createElement('span'); spoken.className = 'visually-hidden'; badge.append(spoken); }
    spoken.textContent = String(count);
  }

  function busy() {
    const active = pending > 0;
    root.toggleAttribute('data-busy', active);
    root.setAttribute('aria-busy', String(active || checkoutPending));
    root.querySelectorAll('[data-rnf-checkout]').forEach(button => { button.disabled = active || !viewReady || !hasRealItems(); });
    const status = root.querySelector('[data-rnf-status]');
    if (status) status.textContent = active ? config.updating : '';
  }

  function showError(message) {
    const node = root.querySelector('[data-rnf-error]');
    if (node) { node.textContent = message || ''; node.hidden = !message; }
  }

  function checkoutFeedback(active) {
    checkoutPending = active;
    root.toggleAttribute('data-rnf-checkout-pending', active);
    root.querySelectorAll('[data-rnf-checkout]').forEach(button => {
      button.setAttribute('aria-busy', String(active));
      button.setAttribute('aria-disabled', String(active));
    });
    // Keep the submitter enabled so its name=checkout is included in the native POST.
    busy();
  }

  function updateTimer() {
    clearTimeout(timerId);
    const node = root.querySelector('[data-rnf-timer]');
    if (!node || !drawer?.isOpen || document.hidden) return;
    if (!timerEnd) {
      try { timerEnd = Number(sessionStorage.getItem('rnf-cart-flash-deadline')); } catch (_) { /* Private storage. */ }
      if (!timerEnd || !Number.isFinite(timerEnd)) {
        timerEnd = Date.now() + 8 * 60 * 1000;
        try { sessionStorage.setItem('rnf-cart-flash-deadline', String(timerEnd)); } catch (_) { /* Keep it for this page. */ }
      }
    }
    const seconds = Math.max(0, Math.ceil((timerEnd - Date.now()) / 1000));
    node.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    // Display only: reaching zero never changes cart prices or discount eligibility.
    if (seconds > 0) timerId = setTimeout(updateTimer, 1000);
  }

  function updateDelivery() {
    const node = root.querySelector('[data-rnf-shipping-text][data-rnf-delivery-template]');
    const template = node && node.getAttribute('data-rnf-delivery-template');
    if (!template) return;
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const start = new Date(today);
    const end = new Date(today);
    start.setDate(today.getDate() + 6);
    end.setDate(today.getDate() + 8);
    let formatter;
    const locale = document.documentElement.lang || undefined;
    try {
      formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
    } catch (_) {
      formatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
    }
    const compactSpanish = /^es(?:-|$)/i.test(locale || '') && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const startText = compactSpanish ? new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(start) : formatter.format(start);
    node.textContent = template.replaceAll('__START__', startText).replaceAll('__END__', formatter.format(end));
  }

  function arrangeStraps(content) {
    const list = content.querySelector('.rnf-cart__items');
    if (!list) return;
    const rows = Array.from(list.children).filter(row => row.hasAttribute('data-rnf-line'));
    const watches = new Map(), associations = new Map(), tails = new Map();
    rows.forEach(row => {
      if (row.dataset.rnfParentWatchVariant) return;
      if (row.dataset.rnfVariant && !watches.has(row.dataset.rnfVariant)) watches.set(row.dataset.rnfVariant, row);
      if (row.dataset.rnfWatchStrapProduct && !associations.has(row.dataset.rnfWatchStrapProduct)) associations.set(row.dataset.rnfWatchStrapProduct, row);
    });
    rows.forEach(row => {
      const requested = row.dataset.rnfParentWatchVariant;
      const parent = requested ? watches.get(requested) : associations.get(row.dataset.rnfProduct);
      const attached = Boolean(parent && parent !== row);
      row.classList.toggle('rnf-cart__item--strap', attached);
      if (!attached) { delete row.dataset.rnfStrapParentVariant; return; }
      row.dataset.rnfStrapParentVariant = parent.dataset.rnfVariant;
      (tails.get(parent) || parent).after(row);
      tails.set(parent, row);
    });
  }

  function preserveImages(content, next) {
    const oldImages = Array.from(content.querySelectorAll('img'));
    const oldRows = Array.from(content.querySelectorAll('[data-rnf-line]'));
    const rows = new Map();
    const preserved = [];
    next.querySelectorAll('[data-rnf-line]').forEach(row => {
      let previous = oldRows.find(old => old.dataset.key === row.dataset.key);
      if (!previous) {
        const item = cart?.items.find(line => line.key === row.dataset.key);
        const matching = item && renderedCart?.items.filter(line => sameLine(line, item));
        // Do not cross identical-looking lines with distinct properties or selling plans.
        if (matching?.length === 1 && cart.items.filter(line => sameLine(line, item)).length === 1) previous = oldRows.find(old => old.dataset.key === matching[0].key);
      }
      if (previous) rows.set(row, previous);
    });
    const attributes = ['src', 'srcset', 'sizes', 'alt', 'width', 'height', 'class', 'loading', 'decoding', 'data-rnf-wheel-source', 'data-rnf-badge-mask'];
    next.querySelectorAll('img').forEach(image => {
      const row = image.closest('[data-rnf-line]');
      const previousRow = row && rows.get(row);
      const index = oldImages.findIndex(old => (row ? previousRow && old.closest('[data-rnf-line]') === previousRow : !old.closest('[data-rnf-line]'))
        && attributes.every(name => (name === 'src' && old.dataset.rnfWheelSource && old.dataset.rnfWheelSource === image.dataset.rnfWheelSource) || old.getAttribute(name) === image.getAttribute(name)));
      if (index >= 0) {
        const placeholder = document.createComment('rnf-cart-image');
        image.replaceWith(placeholder);
        preserved.push([placeholder, oldImages.splice(index, 1)[0]]);
      }
    });
    return preserved;
  }

  function render() {
    const html = sections && sections['rnf-native-cart'];
    const content = root.querySelector('#RnfCartContent');
    if (!html || !content) throw new Error(config.error);
    // Compare server HTML, not just compact JSON: catalog prices, copy and images
    // can change independently of the cart quantities and totals.
    if (html !== renderedHTML) {
      const next = sectionDocument?.querySelector('#RnfCartContent');
      if (!next) throw new Error(config.error);
      arrangeStraps(next);
      const scroller = content.querySelector('[data-rnf-items]');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const focused = root.contains(document.activeElement) ? document.activeElement : null;
      let key = focused && focused.dataset.key;
      if (key && !cart?.items.some(item => item.key === key)) {
        const previous = renderedCart?.items.find(item => item.key === key);
        const matching = previous && cart?.items.filter(item => sameLine(item, previous));
        if (matching?.length === 1 && renderedCart.items.filter(item => sameLine(item, previous)).length === 1) key = matching[0].key;
      }
      const action = focused && ['data-rnf-quantity', 'data-rnf-quantity-input', 'data-rnf-remove', 'data-rnf-protection', 'data-rnf-close', 'data-rnf-items'].find(name => focused.hasAttribute(name));
      const actionValue = action && focused.getAttribute(action);
      const preserved = preserveImages(content, next);
      content.before(next);
      preserved.forEach(([placeholder, image]) => placeholder.replaceWith(image));
      content.remove();
      const nextScroller = next.querySelector('[data-rnf-items]');
      if (nextScroller) nextScroller.scrollTop = scrollTop;
      if (focused && drawer && drawer.isOpen) {
        const selector = action ? `[${action}="${CSS.escape(actionValue)}"]${key ? `[data-key="${CSS.escape(key)}"]` : ''}` : null;
        const target = selector && next.querySelector(selector);
        (target || next.querySelector(pointerInput || drawer.isPage ? '[data-rnf-items]' : '[data-rnf-close]'))?.focus({ preventScroll: true });
      }
      renderedHTML = html;
    }
    renderedCart = cart;
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const bubbleDocument = parseSection(sections['cart-icon-bubble']);
      const bubbleHTML = bubbleDocument.querySelector('.shopify-section')?.innerHTML || sections['cart-icon-bubble'];
      if (bubble.innerHTML !== bubbleHTML) bubble.innerHTML = bubbleHTML;
    }
    paintCartBubble();
    root.classList.toggle('is-empty', !hasRealItems());
    if (!hasWatches() && continuingShopping) rememberContinuingShopping(false);
    viewReady = true;
    syncQuantityControls();
    quantityEdits.forEach(edit => paintQuantity(edit, true));
    paintProtection();
    paintRecommendations();
    busy();
    updateTimer();
    updateDelivery();
    document.dispatchEvent(new CustomEvent('rnf:cart:updated', { detail: { cart, source: 'rnf-native-cart' } }));
  }

  async function updateView() {
    if (!sections || !sections['rnf-native-cart']) await fetchSections();
    render();
  }

  function run(task, options = {}) {
    pending++;
    busy();
    const operation = queue.then(async () => {
      if (!options.quiet) showError('');
      let result;
      let warning = '';
      try {
        result = await task(api);
      } catch (error) {
        cartConfirmedAt = 0;
        // A failed/lost response must never retry an add: it may already have succeeded.
        if (options.recover !== false) try {
          await api.getCart();
          if (options.reconcile !== false) await reconcileGifts();
          if (!options.deferView?.()) await updateView();
        } catch (_) { viewReady = false; /* Keep the last confirmed view and require a refresh. */ }
        if (options.recover === false) viewReady = false;
        if (!options.quiet) showError(error.message || config.error);
        throw error;
      }
      if (options.reconcile !== false) try { await reconcileGifts(); } catch (error) {
        warning = error.message || config.error;
        try { await api.getCart(); } catch (_) { /* Do not repeat a mutation. */ }
      }
      try { if (!options.deferView?.()) await updateView(); } catch (error) {
        console.warn('[RNF cart] Section refresh failed', error);
        viewReady = false; warning = config.error;
      }
      if (options.open !== false && drawer) drawer.reveal(options.opener);
      if (warning && !options.quiet) showError(warning);
      if (options.strict && (warning || !viewReady)) throw new Error(warning || config.error);
      return result;
    });
    queue = operation.catch(() => {}).finally(() => { pending--; busy(); });
    return operation;
  }

  function sameLine(a, b) {
    const properties = value => Object.entries(value || {}).sort(([left], [right]) => left.localeCompare(right));
    return String(a.variant_id) === String(b.variant_id)
      && JSON.stringify(properties(a.properties)) === JSON.stringify(properties(b.properties))
      && String(a.selling_plan_allocation?.selling_plan?.id || '') === String(b.selling_plan_allocation?.selling_plan?.id || '');
  }

  function resolveLine(edit) {
    if (!cart) return null;
    const exact = cart.items.find(item => edit.keys.has(item.key));
    if (exact) return exact;
    const matching = cart.items.filter(item => sameLine(item, edit.line));
    return matching.length === 1 ? matching[0] : null;
  }

  function paintQuantity(edit, optimistic) {
    const line = resolveLine(edit);
    if (line) edit.keys.add(line.key);
    root.querySelectorAll('[data-rnf-quantity-input]').forEach(input => {
      if (edit.keys.has(input.dataset.key)) {
        input.value = String(Math.max(1, optimistic ? edit.quantity : (line?.quantity || 1)));
        input.disabled = Boolean(edit.removeRequested);
      }
    });
    syncQuantityControls();
  }

  function syncQuantityControls() {
    root.querySelectorAll('[data-rnf-quantity]').forEach(button => {
      const edit = quantityEdits.find(item => item.keys.has(button.dataset.key));
      const line = cart?.items.find(item => item.key === button.dataset.key);
      const quantity = edit ? edit.quantity : line?.quantity;
      button.disabled = Boolean(edit?.removeRequested) || (Number(button.dataset.rnfQuantity) < 0 && quantity <= 1);
    });
    root.querySelectorAll('[data-rnf-remove]').forEach(button => {
      button.disabled = quantityEdits.some(edit => edit.removeRequested && edit.keys.has(button.dataset.key));
    });
  }

  function paintProtection() {
    const button = root.querySelector('[data-rnf-protection]');
    if (!button || !protectionIntent) return;
    if (!protectionIntent.buttons.has(button)) protectionIntent.buttons.set(button, button.disabled);
    button.disabled = true;
    button.setAttribute('aria-checked', String(protectionIntent.enabled));
    button.setAttribute('aria-busy', 'true');
    button.toggleAttribute('data-rnf-protection-pending', true);
  }

  function changeLine(key, delta, exact, remove = false) {
    let edit = quantityEdits.find(item => item.keys.has(key));
    const previous = cart && cart.items.find(item => item.key === key);
    if (!edit && previous && cart.items.filter(item => sameLine(item, previous)).length === 1) {
      edit = quantityEdits.find(item => sameLine(item.line, previous));
    }
    if (edit?.removeRequested) return edit.promise || Promise.resolve();
    if (!edit) {
      if (!previous) return Promise.resolve();
      edit = { line: previous, keys: new Set([key]), quantity: previous.quantity, delta: 0, operations: 0, scheduled: null };
      quantityEdits.push(edit);
    }
    const previousQuantity = edit.quantity;
    if (remove) { edit.removeRequested = true; edit.exact = 0; edit.delta = 0; }
    else if (exact !== undefined) { edit.quantity = Math.max(1, exact); edit.exact = edit.quantity; edit.delta = 0; }
    else edit.quantity = Math.max(1, previousQuantity + delta);
    if (!remove && exact === undefined && edit.exact !== undefined) edit.exact = Math.max(1, edit.exact + delta);
    else if (!remove && exact === undefined) edit.delta += edit.quantity - previousQuantity;
    paintQuantity(edit, true);
    if (edit.scheduled) return edit.promise;
    const scheduled = {};
    edit.scheduled = scheduled;
    edit.operations++;
    edit.promise = run(async tx => {
      await currentCart();
      const line = resolveLine(edit);
      const quantity = edit.removeRequested ? 0 : (edit.exact === undefined ? Math.max(1, (line?.quantity || 1) + edit.delta) : edit.exact);
      if (!edit.removeRequested) edit.quantity = quantity;
      edit.exact = undefined;
      edit.delta = 0;
      if (edit.scheduled === scheduled) edit.scheduled = null;
      paintQuantity(edit, true);
      if (!line) return cart;
      edit.keys.add(line.key);
      if (line.quantity === quantity) return cart;
      if (quantity === 0 && config.update) {
        const remaining = { items: cart.items.filter(item => item.key !== line.key) };
        return removeLines({ [line.key]: 0, ...cleanupRemovals(remaining) });
      }
      return tx.change({ id: line.key, quantity });
    }, { open: false, deferView: () => edit.operations > 1 }).finally(() => {
      if (edit.scheduled === scheduled) edit.scheduled = null;
      if (--edit.operations === 0) {
        edit.removeRequested = false;
        paintQuantity(edit, false);
        quantityEdits.splice(quantityEdits.indexOf(edit), 1);
        syncQuantityControls();
      }
    });
    return edit.promise;
  }

  function paintRecommendations() {
    root.querySelectorAll('[data-rnf-recommendation-add]').forEach(button => {
      const intent = recommendationIntents.get(button.dataset.rnfVariant);
      if (!intent) return;
      const select = button.closest('[data-rnf-recommendation-card]')?.querySelector('[data-rnf-recommendation-variant]');
      for (const node of [button, select]) {
        if (!node) continue;
        if (!intent.nodes.has(node)) intent.nodes.set(node, node.disabled);
        node.disabled = true;
      }
      button.setAttribute('aria-busy', 'true');
      button.toggleAttribute('data-rnf-recommendation-pending', true);
    });
  }

  function updateRecommendation(select) {
    if (select.disabled) return;
    const card = select.closest('[data-rnf-recommendation-card]');
    const option = select.selectedOptions?.[0];
    if (!card || !option) return;
    card.querySelector('[data-rnf-recommendation-add]')?.setAttribute('data-rnf-variant', select.value);
    const price = card.querySelector('[data-rnf-recommendation-price]');
    const compare = card.querySelector('[data-rnf-recommendation-compare]');
    const image = card.querySelector('[data-rnf-recommendation-image]');
    if (price && option.dataset.rnfPrice !== undefined) price.textContent = option.dataset.rnfPrice;
    if (compare) { compare.textContent = option.dataset.rnfCompare || ''; compare.hidden = !option.dataset.rnfCompare; }
    if (image && option.dataset.rnfImage) {
      image.removeAttribute('srcset');
      image.toggleAttribute('data-rnf-badge-mask', option.dataset.rnfBadgeMask === 'true');
      const photo = window.__rnfCarouselMedia?.wheelSource(option.dataset.rnfImage);
      image.removeAttribute('data-rnf-wheel-source');
      if (photo && !image.closest('[data-rnf-automatic-media]')) image.dataset.rnfWheelSource = option.dataset.rnfImage;
      image.src = photo && !image.closest('[data-rnf-automatic-media]') ? photo : option.dataset.rnfImage;
      const spin = card.querySelector('[data-rnf-drawer-spin]');
      if (spin) spin.hidden = option.dataset.rnfSpinAllowed === 'false' || (!!spin.dataset.positions && !spin.dataset.positions.split(',').includes(option.dataset.rnfMediaPosition || '1'));
    }
  }

  function addRecommendation(button) {
    const select = button.closest('[data-rnf-recommendation-card]')?.querySelector('[data-rnf-recommendation-variant]');
    const variant = String(select?.value || button.dataset.rnfVariant || '');
    if (button.disabled || !/^[1-9]\d*$/.test(variant) || recommendationIntents.has(variant)) return Promise.resolve();
    button.setAttribute('data-rnf-variant', variant);
    const intent = { nodes: new Map() };
    recommendationIntents.set(variant, intent);
    paintRecommendations();
    return run(tx => tx.add({ items: [{ id: variant, quantity: 1 }] }), { open: false }).finally(() => {
      intent.nodes.forEach((disabled, node) => {
        node.disabled = disabled;
        node.setAttribute('aria-busy', 'false');
        node.toggleAttribute('data-rnf-recommendation-pending', false);
      });
      recommendationIntents.delete(variant);
    });
  }

  function scrollRecommendations(button) {
    const track = root.querySelector('[data-rnf-recommendations]');
    if (!track) return;
    const style = window.getComputedStyle(track);
    const card = track.querySelector('[data-rnf-recommendation-card]');
    const distance = (card?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(style.columnGap) || 0);
    track.scrollBy({ left: Number(button.dataset.rnfRecommendationScroll) * distance * (style.direction === 'rtl' ? -1 : 1), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  async function prepareCheckout() {
    // This explicit purchase action confirms the real cart, then reconciles it.
    // Passive synchronization deliberately remains read-only.
    await run(tx => tx.getCart(), { open: false, strict: true });
    await queue;
    if (pending || !viewReady || !hasRealItems() || needsGiftReconciliation()) throw new Error(config.error);
    return cart;
  }

  const isReady = () => viewReady && !needsGiftReconciliation();
  window.RNFCart = { run, prepareCheckout, isGiftOnlyVariant, refresh: () => run(() => fetchSections(), { open: false }), sync: syncCart, isReady, isContinuingShopping: () => isReady() && continuingShopping && hasWatches() };

  function saveStyles(element, names) {
    return names.map(name => [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
  }

  function restoreStyles(element, saved) {
    saved.forEach(([name, value, priority]) => {
      if (value) element.style.setProperty(name, value, priority);
      else element.style.removeProperty(name);
    });
  }

  function transitionTime(style, propertyName) {
    const times = value => (value || '0s').split(',').map(part => parseFloat(part) * (part.trim().endsWith('ms') ? 1 : 1000) || 0);
    const durations = times(style.transitionDuration), delays = times(style.transitionDelay);
    return Math.max(0, ...(style.transitionProperty || 'all').split(',').map((property, index) =>
      ['all', propertyName].includes(property.trim()) ? durations[index % durations.length] + delays[index % delays.length] : 0));
  }

  class RnfCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.eventCleanup = [];
      const listen = (target, type, handler, options) => {
        target.addEventListener(type, handler, options);
        this.eventCleanup.push(() => target.removeEventListener(type, handler, options));
      };
      drawer = this;
      this.isPage = this.hasAttribute('data-rnf-cart-page');
      this.dialog = this.querySelector('dialog') || this.querySelector('[data-rnf-cart-panel]');
      if (!this.isPage) {
        listen(this.dialog, 'cancel', event => { event.preventDefault(); this.close(true); });
        listen(this.dialog, 'click', event => { if (event.target === this.dialog) this.close(true); });
        listen(this.dialog, 'close', () => { if (!this.isOpen) this.finishClose(); });
      }
      listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-rnf-close')) { this.close(true); return; }
        if (checkoutPending || button.disabled) return;
        if (button.hasAttribute('data-rnf-quantity')) {
          changeLine(button.dataset.key, Number(button.dataset.rnfQuantity)).catch(() => {});
        } else if (button.hasAttribute('data-rnf-remove')) {
          changeLine(button.dataset.key, 0, 0, true).catch(() => {});
        } else if (button.hasAttribute('data-rnf-protection')) {
          this.toggleProtection(button).catch(() => {});
        } else if (button.hasAttribute('data-rnf-recommendation-add')) {
          addRecommendation(button).catch(() => {});
        } else if (button.hasAttribute('data-rnf-recommendation-scroll')) {
          scrollRecommendations(button);
        }
      });
      listen(this, 'change', event => {
        if (checkoutPending) return;
        const select = event.target.closest('[data-rnf-recommendation-variant]');
        if (select) { updateRecommendation(select); return; }
        const input = event.target.closest('[data-rnf-quantity-input]');
        if (input && !input.disabled) {
          const quantity = Math.max(1, Number(input.value));
          if (Number.isInteger(quantity)) { input.value = String(quantity); changeLine(input.dataset.key, 0, quantity).catch(() => {}); }
          else if (cart) input.value = cart.items.find(item => item.key === input.dataset.key)?.quantity || 1;
        }
      });
      listen(this, 'keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-rnf-quantity-input]')) {
          event.preventDefault();
          event.target.blur();
        }
      });
      listen(this, 'submit', async event => {
        if (approvedCheckoutForm === event.target) {
          approvedCheckoutForm = null; // One native resubmission only.
          approvedCheckoutEvent = event;
          if (event.defaultPrevented || pending || !viewReady || !hasRealItems() || needsGiftReconciliation()) {
            event.preventDefault();
            checkoutFeedback(false);
          }
          return;
        }
        if (event.defaultPrevented) return;
        event.preventDefault();
        if (pending || !viewReady || checkoutPending) return;
        const attempt = ++checkoutAttempt;
        checkoutFeedback(true);
        try {
          await prepareCheckout();
          if (attempt !== checkoutAttempt || !this.isConnected) return;
          // Reconciliation replaces the form; never submit the detached original.
          const button = this.querySelector('[data-rnf-checkout]');
          const form = button?.form;
          if (!form || button.disabled || !hasRealItems()) throw new Error(config.error);
          approvedCheckoutForm = form;
          approvedCheckoutEvent = null;
          try { form.requestSubmit(button); }
          finally {
            approvedCheckoutForm = null;
            // requestSubmit returns without throwing if validity fails or
            // another listener cancels the event. Allow a corrected retry.
            if (!approvedCheckoutEvent || approvedCheckoutEvent.defaultPrevented) checkoutFeedback(false);
            approvedCheckoutEvent = null;
          }
        } catch (error) {
          if (attempt !== checkoutAttempt) return;
          checkoutFeedback(false);
          showError(error.message || config.error);
        }
      });
      listen(window, 'pageshow', event => {
        checkoutAttempt++;
        approvedCheckoutForm = null;
        approvedCheckoutEvent = null;
        checkoutFeedback(false);
        if (event.persisted || !cartConfirmedAt) {
          cartConfirmedAt = 0;
          syncCart().catch(() => {});
        }
      });
      listen(window, 'pagehide', suspendSync);
      listen(document, 'pointerdown', () => { pointerInput = true; }, { passive: true });
      listen(document, 'keydown', () => { pointerInput = false; });
      listen(document, 'click', event => {
        const link = event.target.closest('a');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
        const url = new URL(link.href, location.href);
        if (url.origin === location.origin && url.pathname.replace(/\/$/, '') === config.cartPage.replace(/\/$/, '')) {
          event.preventDefault();
          this.open(link);
        }
      });
      listen(document, 'cart:refresh', event => {
        if (event.detail?.source !== 'rnf-native-cart') { cartConfirmedAt = 0; window.RNFCart.refresh().catch(() => {}); }
      });
      listen(document, 'visibilitychange', () => {
        updateTimer();
        if (document.hidden) suspendSync();
        else { cartConfirmedAt = 0; updateDelivery(); syncCart().catch(() => {}); }
      });
      listen(window, 'online', () => { cartConfirmedAt = 0; syncCart().catch(() => {}); });
      listen(document, 'shopify:section:unload', event => {
        if (event.target === this || event.target?.contains?.(this)) this.finishClose(false);
      });
      arrangeStraps(this);
      syncQuantityControls();
      busy();
      syncCart().catch(() => {});
    }

    disconnectedCallback() {
      suspendSync();
      this.finishClose(false);
      this.eventCleanup?.forEach(remove => remove());
      this.eventCleanup = [];
      this.initialized = false;
    }

    get isOpen() { return this.isPage || this.dialog.open; }
    setActiveElement(element) { this.opener = element; }
    getSectionsToRender() { return sectionIds.map(id => ({ id, selector: id === 'rnf-native-cart' ? '#RnfCartContent' : '.shopify-section' })); }

    reveal(opener) {
      if (!this.isConnected) return;
      updateDelivery();
      if (this.isPage) { updateTimer(); return; }
      if (this.isOpen) {
        if (this.closeToken) {
          this.cancelClose();
          this.dialog.setAttribute('data-rnf-visible', '');
          updateTimer();
        }
        return;
      }
      this.opener = opener || this.opener || document.activeElement;
      this.lockScroll();
      try { this.dialog.showModal(); } catch (error) { this.finishClose(false); throw error; }
      // Commit the hidden starting state before revealing; reopening reverses the
      // same CSS transition from its current opacity and vertical position.
      window.getComputedStyle(this.dialog).transform;
      window.getComputedStyle(this.dialog).opacity;
      window.getComputedStyle(this.dialog, '::backdrop').opacity;
      this.openFrame = window.requestAnimationFrame(() => {
        this.openFrame = null;
        if (this.isOpen && !this.closeToken) this.dialog.setAttribute('data-rnf-visible', '');
      });
      updateTimer();
      this.querySelector(pointerInput ? '[data-rnf-items]' : '[data-rnf-close]')?.focus({ preventScroll: true });
    }

    async open(opener) {
      if (!this.isConnected) return Promise.resolve();
      if (!pending && viewReady && cart && cartConfirmedAt && Date.now() - cartConfirmedAt <= 5000 && !needsGiftReconciliation()) {
        this.reveal(opener);
        return cart;
      }
      try {
        await syncCart();
        if (!this.isConnected || document.hidden) return cart;
        if (needsGiftReconciliation()) await run(() => cart, { open: false });
        if (this.isConnected && !document.hidden) this.reveal(opener);
        return cart;
      } catch (_) {
        if (this.isConnected && !document.hidden) location.assign(config.cartPage);
      }
    }

    close(continueShopping = false) {
      if (this.isPage) return;
      if (!this.isOpen || this.closeToken) return;
      if (continueShopping && hasWatches()) rememberContinuingShopping(true);
      window.cancelAnimationFrame(this.openFrame);
      this.openFrame = null;
      const wasVisible = this.dialog.hasAttribute('data-rnf-visible');
      const parts = ['', '::backdrop'].map(pseudo => ({ pseudo, opacity: Number(window.getComputedStyle(this.dialog, pseudo || null).opacity) }));
      this.dialog.removeAttribute('data-rnf-visible');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this.finishClose(); return; }
      const tracks = parts.flatMap(part => {
        const style = window.getComputedStyle(this.dialog, part.pseudo || null);
        return (part.pseudo ? ['opacity'] : ['opacity', 'transform']).map(property => ({
          pseudo: part.pseudo, property,
          active: property === 'transform' ? wasVisible : part.opacity !== 0,
          duration: transitionTime(style, property)
        }));
      });
      const waiting = new Set(tracks.filter(track => track.active && track.duration > 0).map(track => `${track.pseudo}:${track.property}`));
      const duration = Math.max(...tracks.map(track => track.duration));
      if (!waiting.size) { this.finishClose(); return; }
      const token = {};
      this.closeToken = token;
      this.dialog.setAttribute('data-rnf-closing', '');
      this.closeTransition = event => {
        const pseudo = event.pseudoElement || '';
        const key = `${pseudo}:${event.propertyName}`;
        if (event.target !== this.dialog || this.closeToken !== token || !waiting.has(key)) return;
        if (event.propertyName === 'opacity' && Number(window.getComputedStyle(this.dialog, pseudo || null).opacity) > 0) return;
        // A queued event from an interrupted opening cannot finish a live exit.
        if (event.propertyName === 'transform' && (this.dialog.getAnimations?.() || []).some(animation => animation.transitionProperty === 'transform' && animation.playState === 'running')) return;
        waiting.delete(key);
        if (!waiting.size) this.finishClose();
      };
      this.dialog.addEventListener('transitionend', this.closeTransition);
      this.closeTimer = setTimeout(() => { if (this.closeToken === token) this.finishClose(); }, duration + 50);
    }

    cancelClose() {
      this.closeToken = null;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
      if (this.closeTransition) this.dialog.removeEventListener('transitionend', this.closeTransition);
      this.closeTransition = null;
      this.dialog.removeAttribute('data-rnf-closing');
    }

    finishClose(restoreFocus = true) {
      if (this.isPage) { clearTimeout(timerId); return; }
      this.cancelClose();
      window.cancelAnimationFrame(this.openFrame);
      this.openFrame = null;
      this.dialog.removeAttribute('data-rnf-visible');
      clearTimeout(timerId);
      const lock = this.scrollLock;
      this.scrollLock = null;
      if (this.isOpen) this.dialog.close();
      if (!lock) return;
      lock.removeListeners.forEach(remove => remove());
      const html = document.documentElement, body = document.body;
      restoreStyles(html, lock.html);
      body.classList.toggle('cart-drawer-open', lock.bodyClass);
      html.classList.toggle('cart-drawer-open', lock.htmlClass);
      if (window.scrollX !== lock.x || window.scrollY !== lock.y) {
        const behavior = saveStyles(html, ['scroll-behavior']);
        html.style.setProperty('scroll-behavior', 'auto', 'important');
        window.scrollTo(lock.x, lock.y);
        restoreStyles(html, behavior);
      }
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    lockScroll() {
      if (this.scrollLock) return;
      const body = document.body, html = document.documentElement;
      const overflow = ['overflow', 'overflow-x', 'overflow-y', 'overscroll-behavior', 'overscroll-behavior-x', 'overscroll-behavior-y', 'scrollbar-gutter'];
      const lock = this.scrollLock = {
        x: window.scrollX, y: window.scrollY,
        html: saveStyles(html, overflow),
        bodyClass: body.classList.contains('cart-drawer-open'), htmlClass: html.classList.contains('cart-drawer-open'),
        removeListeners: []
      };
      // Keep the sticky header and document at their real scroll position.
      body.classList.add('cart-drawer-open');
      html.classList.add('cart-drawer-open');
      html.style.setProperty('scrollbar-gutter', 'stable', 'important');
      html.style.setProperty('overflow', 'hidden', 'important');
      html.style.setProperty('overscroll-behavior', 'none', 'important');
      const listen = (type, handler, passive) => {
        document.addEventListener(type, handler, { capture: true, passive });
        lock.removeListeners.push(() => document.removeEventListener(type, handler, true));
      };
      const canScroll = (target, dx, dy) => {
        if (!this.dialog.contains(target)) return false;
        const horizontal = Math.abs(dx) > Math.abs(dy), delta = horizontal ? dx : dy;
        if (!delta) return true;
        for (let node = target; node && node !== this.dialog; node = node.parentElement) {
          if (node.nodeType && node.nodeType !== 1) continue;
          const style = window.getComputedStyle(node);
          if (!/auto|scroll/.test(horizontal ? style.overflowX : style.overflowY)) continue;
          const range = horizontal ? node.scrollWidth - node.clientWidth : node.scrollHeight - node.clientHeight;
          if (range <= 1) continue;
          const position = horizontal ? node.scrollLeft : node.scrollTop;
          const start = horizontal && style.direction === 'rtl' ? -range : 0;
          const end = horizontal && style.direction === 'rtl' ? 0 : range;
          if (delta < 0 ? position > start + 1 : position < end - 1) return true;
        }
        return false;
      };
      const guard = (event, dx, dy) => { if (event.cancelable !== false && !canScroll(event.target, dx, dy)) event.preventDefault(); };
      listen('wheel', event => { if (!event.ctrlKey) guard(event, event.deltaX, event.deltaY); }, false);
      let touch;
      listen('touchstart', event => { touch = event.touches.length === 1 ? event.touches[0] : null; }, true);
      listen('touchmove', event => {
        if (event.touches.length !== 1) { touch = null; return; }
        const next = event.touches[0];
        if (touch) guard(event, touch.clientX - next.clientX, touch.clientY - next.clientY);
        touch = next;
      }, false);
      listen('touchend', () => { touch = null; }, true);
      listen('touchcancel', () => { touch = null; }, true);
    }

    renderContents() {
      // An external add response can arrive after a newer queued write. Read once
      // at its queue position; native RNF mutations already use their bundled HTML.
      return run(() => fetchSections(), { opener: this.opener });
    }

    toggleProtection(button) {
      if (protectionIntent || button.disabled || !hasRealItems()) return Promise.resolve();
      protectionIntent = { enabled: button.getAttribute('aria-checked') !== 'true', buttons: new Map() };
      paintProtection();
      return run(async tx => {
        const current = await currentCart();
        const lines = current.items.filter(item => String(item.variant_id) === config.protection);
        if (!protectionIntent.enabled && lines.length) {
          if (config.update && lines.length > 1) {
            await removeLines(Object.fromEntries(lines.map(line => [line.key, 0])));
            return;
          }
          let remaining = lines.length;
          while (remaining-- > 0) {
            const line = cart.items.find(item => String(item.variant_id) === config.protection);
            if (!line) break;
            await tx.change({ id: line.key, quantity: 0 });
            if (!cart) await tx.getCart();
          }
        } else if (protectionIntent.enabled && !lines.length && hasRealItems()) {
          await tx.add({ items: [{ id: config.protection, quantity: 1 }] });
        }
      }, { open: false }).finally(() => {
        const enabled = Boolean(cart?.items.some(item => String(item.variant_id) === config.protection));
        protectionIntent.buttons.forEach((disabled, currentButton) => {
          currentButton.disabled = disabled;
          currentButton.setAttribute('aria-checked', String(enabled));
          currentButton.setAttribute('aria-busy', 'false');
          currentButton.toggleAttribute('data-rnf-protection-pending', false);
        });
        protectionIntent = null;
      });
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', RnfCartDrawer);
})();








